import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk, faXmark } from '@fortawesome/free-solid-svg-icons'
import type { SubCompetency } from '../../types/GrowthWheelManagement'
import { RichTextEditor } from './RichTextEditor'

/** True when name, description, and levels 1–5 match (used for dirty detection). */
export function subCompetencyEditableContentEqual(
  a: SubCompetency,
  b: SubCompetency,
): boolean {
  if (a.name !== b.name || a.description !== b.description) return false
  for (let level = 1; level <= 5; level++) {
    if (
      (a.levelDescriptions[level] ?? '') !== (b.levelDescriptions[level] ?? '')
    ) {
      return false
    }
  }
  return true
}

type Props = {
  editedSubCompetency: SubCompetency
  baselineSubCompetency: SubCompetency
  setEditedSubCompetency: React.Dispatch<
    React.SetStateAction<SubCompetency | null>
  >
  roleName?: string
  parentCompetencyName?: string
  onClose: () => void
  onSave: () => void
}

export function EditSubCompetencyPanel({
  editedSubCompetency,
  baselineSubCompetency,
  setEditedSubCompetency,
  roleName,
  parentCompetencyName,
  onClose,
  onSave,
}: Props) {
  const titleOk = Boolean(editedSubCompetency.name.trim())
  const hasChanges = !subCompetencyEditableContentEqual(
    editedSubCompetency,
    baselineSubCompetency,
  )
  const canSave = titleOk && hasChanges

  return (
    <div className="gw-wm__panel-backdrop">
      <div className="gw-wm__panel">
        <div className="gw-wm__panel-header">
          <div className="gw-wm__panel-header-row">
            <div>
              <h3 className="gw-wm__panel-title">Edit Sub-Competency</h3>
              <p className="gw-wm__panel-breadcrumb">
                {roleName && (
                  <span className="gw-wm__role-tag">{roleName}</span>
                )}
                {roleName && ' › '}
                {parentCompetencyName}
              </p>
            </div>
            <button
              type="button"
              className="gw-wm__btn-icon"
              onClick={onClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        <div className="gw-wm__panel-body">
          <div className="gw-wm__form-stack">
            <div>
              <label className="gw-wm__label">Sub-competency name</label>
              <input
                type="text"
                className="gw-wm__input"
                value={editedSubCompetency.name}
                onChange={e =>
                  setEditedSubCompetency({
                    ...editedSubCompetency,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="gw-wm__label">Description</label>
              <RichTextEditor
                value={editedSubCompetency.description}
                onChange={val =>
                  setEditedSubCompetency({
                    ...editedSubCompetency,
                    description: val,
                  })
                }
                placeholder="Describe this sub-competency..."
              />
            </div>
          </div>

          <div>
            <h4
              className="gw-wm__card-title"
              style={{ marginBottom: '0.25rem' }}
            >
              Levels 1–5
            </h4>
            <p className="gw-wm__hint mb-3">The specific rubric definitions.</p>
            <div className="gw-wm__form-stack">
              {[1, 2, 3, 4, 5].map(level => {
                const description =
                  editedSubCompetency.levelDescriptions[level] || ''
                return (
                  <div key={level} className="gw-wm__level-block">
                    <div className="gw-wm__level-label">Level {level}</div>
                    <RichTextEditor
                      value={description}
                      onChange={val =>
                        setEditedSubCompetency({
                          ...editedSubCompetency,
                          levelDescriptions: {
                            ...editedSubCompetency.levelDescriptions,
                            [level]: val,
                          },
                        })
                      }
                      placeholder={`Rubric definition for level ${level}…`}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="gw-wm__panel-footer">
          <div className="gw-wm__modal-actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="gw-wm__btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gw-wm__btn-primary"
              disabled={!canSave}
              onClick={onSave}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
